import { Injectable } from '@angular/core';
import { LabelledMatrixValues, LabelledValues } from 'app/trello/models/charts';
import { DateTime } from 'luxon';

export enum GroupLevel {
  WEEK = 'week',
  MONTH = 'month',
  QUARTER = 'quarter',
  HALF_YEAR = 'halfYear',
  YEAR = 'year',
}

const groupLevels: GroupLevel[] = [
  GroupLevel.WEEK,
  GroupLevel.MONTH,
  GroupLevel.QUARTER,
  GroupLevel.HALF_YEAR,
  GroupLevel.YEAR,
];

@Injectable({
  providedIn: 'root'
})
export class DataMatrixMapper {

  private readonly fiveDayLabels: string[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
  private readonly sevenDayLabels: string[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  private readonly groupFloorFunctions: { [key in GroupLevel]: (date: DateTime) => DateTime } = {

    [GroupLevel.WEEK]: (date: DateTime) => date.minus({days: date.weekday - 1}).startOf('day'),

    [GroupLevel.MONTH]: (date: DateTime) => date.startOf('month'),

    [GroupLevel.QUARTER]: (date: DateTime) => {

      const quarterStartMonth = Math.floor((date.month - 1) / 3) * 3 + 1;

      return DateTime.fromObject({year: date.year, month: quarterStartMonth, day: 1});
    },

    [GroupLevel.HALF_YEAR]: (date: DateTime) => {

      const halfStartMonth = date.month <= 6 ? 1 : 7;

      return DateTime.fromObject({year: date.year, month: halfStartMonth, day: 1});
    },

    [GroupLevel.YEAR]: (date: DateTime) => date.startOf('year'),
  };

  private readonly groupIncrementFunctions: { [key in GroupLevel]: (date: DateTime) => DateTime } = {
    [GroupLevel.WEEK]: (date: DateTime) => date.plus({weeks: 1}),
    [GroupLevel.MONTH]: (date: DateTime) => date.plus({months: 1}),
    [GroupLevel.QUARTER]: (date: DateTime) => date.plus({months: 3}),
    [GroupLevel.HALF_YEAR]: (date: DateTime) => date.plus({months: 6}),
    [GroupLevel.YEAR]: (date: DateTime) => date.plus({years: 1}),
  };

  private groupDecrementFunctions: { [key in GroupLevel]: (dt: DateTime, count: number) => DateTime } = {
    [GroupLevel.WEEK]: (date: DateTime, count: number) => date.minus({weeks: count}),
    [GroupLevel.MONTH]: (date: DateTime, count: number) => date.minus({months: count}),
    [GroupLevel.QUARTER]: (date: DateTime, count: number) => date.minus({months: count * 3}),
    [GroupLevel.HALF_YEAR]: (date: DateTime, count: number) => date.minus({months: count * 6}),
    [GroupLevel.YEAR]: (date: DateTime, count: number) => date.minus({years: count}),
  };

  public mapData(
    dateLabelledValues: LabelledValues,
    fixedStartDate?: string,
    fixedEndDate?: string,
    includeWeekends: boolean = false,
  ): { groupLevel: GroupLevel, data: LabelledMatrixValues, } {

    const dataPoints =
      dateLabelledValues.labels
        .map((formattedDate: string, index: number) => ({
          date: DateTime.fromISO(formattedDate),
          value: dateLabelledValues.values[index],
        }));

    const minDate =
      fixedStartDate
        ? DateTime.fromISO(fixedStartDate)
        : dataPoints.reduce(
          (min, cur) => (cur.date < min ? cur.date : min),
          dataPoints[0].date
        );

    const maxDate =
      fixedEndDate
        ? DateTime.fromISO(fixedEndDate)
        : dataPoints.reduce(
          (max, cur) => (cur.date > max ? cur.date : max),
          dataPoints[0].date
        );

    // select the appropriate grouping level
    // the levels are tried in order until the number of periods is ≤ 52
    // (except for weekly, where we allow 53)

    let chosenLevel: GroupLevel = GroupLevel.YEAR; // default fallback

    let periodStarts: DateTime[] = [];

    for (const groupLevel of groupLevels) {

      const floor = this.groupFloorFunctions[groupLevel];
      const increment = this.groupIncrementFunctions[groupLevel];

      const startPeriod = floor(minDate);

      const periods: DateTime[] = [];

      let current = startPeriod;

      while (current <= maxDate) {

        periods.push(current);
        current = increment(current);
      }

      if (groupLevel === GroupLevel.WEEK) {

        // for weekly grouping, allow up to 53 periods (53 weeks in leap year)
        if (periods.length <= 53) {

          chosenLevel = groupLevel;
          periodStarts = periods;

          break;
        }

      } else {

        if (periods.length <= 52) {

          chosenLevel = groupLevel;
          periodStarts = periods;

          break;
        }
      }
    }

    let totalColumns = 52;
    if (chosenLevel === GroupLevel.WEEK && periodStarts.length === 53) {
      totalColumns = 53;
    }

    if (periodStarts.length < totalColumns) {

      const missingLabels = totalColumns - periodStarts.length;

      const start = this.groupDecrementFunctions[chosenLevel](periodStarts[0], missingLabels);
      const timeline: DateTime[] = [];

      let current = start;
      for (let column = 0; column < totalColumns; column++) {

        timeline.push(current);

        current = this.groupIncrementFunctions[chosenLevel](current);
      }

      periodStarts = timeline;
    }

    const xLabels: string[] = periodStarts.map((date) => date.toISODate()!);
    const yLabels = includeWeekends ? this.sevenDayLabels : this.fiveDayLabels;
    const values: number[][] = [];

    for (let column = 0; column < totalColumns; column++) {
      values[column] = new Array(yLabels.length).fill(0);
    }

    const periodBoundaries =
      periodStarts.map((start) => {

        const end = this.groupIncrementFunctions[chosenLevel](start).minus({milliseconds: 1});

        return {start, end};
      });

    dataPoints.forEach(({date, value}) => {

      const weekday = date.weekday;
      const weekend = weekday > 5;

      if (!includeWeekends && weekend) {
        return;
      }

      const periodIndex =
        periodBoundaries.findIndex((boundary) =>
          date >= boundary.start && date <= boundary.end
        );

      if (periodIndex === -1) {
        return;
      }

      const rowIndex = weekday - 1;

      values[periodIndex][rowIndex] += value;
    });

    return {
      groupLevel: chosenLevel,
      data: {
        xLabels,
        yLabels,
        values,
      }
    };
  }
}
